import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import BucketLock from "@/services/bucket/bucket-lock";
import { BucketLockSettings } from "@/dtos/BucketLock.dto";

export interface BucketLockSettingsParams {
  organizationId: string;
}

export function useBucketLockSettings(
  params: Ref<BucketLockSettingsParams | null> | BucketLockSettingsParams | null
) {
  const queryKey = computed(() => {
    const p = unref(params);
    if (!p) return ["bucketLockSettings", "disabled"];
    return ["bucketLockSettings", p.organizationId];
  });

  const enabled = computed(() => {
    const p = unref(params);
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async (): Promise<BucketLockSettings> => {
      const p = unref(params);
      if (!p) throw new Error("Parameters not ready");

      const result = await BucketLock.getBucketLockSettings({
        organizationId: p.organizationId,
      });

      let data = null;
      if (result.data?.status && result.data?.data) {
        data = result.data.data;
      } else if (result.data && !result.data.status) {
        data = result.data;
      }

      if (data) {
        return {
          enabled: data.enabled ?? false,
          priorityOrder: data.priorityOrder ?? [],
        };
      }

      return {
        enabled: false,
        priorityOrder: [],
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
